import { FC, useMemo, useState } from 'react';
import { GET_TOTAL_NUMBER_OF_USER_CONTACTS, GET_TOTAL_NUMBER_OF_USER_CONTACTS_IN_ANY_PLAYBOOK } from '@zaplify/graphql';
import { useQuery } from '@apollo/client';
import { PageHeader } from '../../../components/page-header';
import { Button } from '@shadcn/ui/components/ui/button';
import { Eye, PanelLeft, Settings, Upload } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth } from '../../../providers/authentication-provider';
import { PlanCode } from '@zaplify/subscriptions';
import { Badge } from '@shadcn/ui/components/ui/badge';
import { paths } from '../../../../routes/paths';
import { PlaybooksModal } from '../../playbooks-2/components/playbooks-modal';
import { usePlaybookManagement } from '../../../hooks/use-playbook-management';

interface ContactsPageHeaderProps {}

const ContactsPageHeader: FC<ContactsPageHeaderProps> = ({}) => {
    const { actionsV2: actionsV2Enabled } = useFlags();
    const { loading: isContactsLoading, data: contactsData } = useQuery(
        actionsV2Enabled ? GET_TOTAL_NUMBER_OF_USER_CONTACTS : GET_TOTAL_NUMBER_OF_USER_CONTACTS_IN_ANY_PLAYBOOK,
        {
            fetchPolicy: 'network-only',
        }
    );
    const { prospectId } = useParams();
    const {
        authState: { isGlobalAdmin },
    } = useAuth();
    const navigate = useNavigate();
    const {
        authState: {
            tokenResult: {
                claims: { plan },
            },
        },
    } = useAuth();

    const canAccessListImport = plan !== PlanCode.FREE;

    const totalContacts = useMemo(() => {
        return contactsData?.UserContacts_aggregate?.aggregate?.count || 0;
    }, [contactsData]);

    return (
        <PageHeader
            title="My contacts"
            count={totalContacts}
            isLoadingCount={isContactsLoading}
            rightElement={
                <div className="flex items-center gap-2">
                    {isGlobalAdmin && (
                        <Button
                            variant="outline"
                            className="gap-2 relative"
                            onClick={() => {
                                navigate(paths.NEW.INSIGHTS);
                            }}
                        >
                            <Eye className="h-4 w-4" />
                            Insights
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        className="gap-2 relative"
                        disabled={!canAccessListImport}
                        onClick={() => {
                            if (canAccessListImport) {
                                navigate(paths.NEW.IMPORT_PATHS.LIST);
                            }
                        }}
                    >
                        <Upload className="h-4 w-4" />
                        Upload contacts
                        {!canAccessListImport && (
                            <Badge
                                variant="secondary"
                                className="ml-2 text-xs py-0.5 px-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                            >
                                PRO
                            </Badge>
                        )}
                    </Button>
                    <ManagePlaybooksButton />
                    {prospectId && (
                        <Button variant="ghost" size="icon" onClick={() => navigate(paths.NEW.CONTACTS)}>
                            <PanelLeft />
                        </Button>
                    )}
                </div>
            }
        />
    );
};

const ManagePlaybooksButton: FC = () => {
    const { actionsV2 } = useFlags();
    const { isModalOpen, openManagePlaybooks, closeModal, shouldShowModal } = usePlaybookManagement();

    return (
        <>
            {actionsV2 ? (
                <Button variant="outline" className="gap-2" onClick={openManagePlaybooks}>
                    <Settings className="h-4 w-4" />
                    Manage Playbooks
                </Button>
            ) : null}
            {shouldShowModal && <PlaybooksModal open={isModalOpen} onOpenChange={closeModal} />}
        </>
    );
};

export default ContactsPageHeader;
